import { appUrlsConfig } from './app-urls.config';
import { axiosService } from './axios.service';

/**
 * PFS admin analytics.
 *
 * Two endpoints cover the whole dashboard. `overview` returns every dimension for one window in
 * a single response - totals, the daily series, product lines, markets and the package
 * leaderboard - which is the difference from the legacy Revenue Report, where
 * `RevenueReportService::getItemType()` aborts unless exactly one dimension is chosen and the
 * same window has to be re-queried to see it another way. The dashboard fetches it as two halves,
 * a quick summary and the slower details, so the page can paint before the breakdowns are built.
 *
 * The server owns every number here. Nothing in this file computes revenue, a share or a change.
 */

/** How the daily series is split. */
export const SERIES_GROUP = {
  /** One line for the whole business. */
  TOTAL: 'total',
  /** Food Handler, Food Manager, Alcohol… */
  PRODUCT_LINE: 'product_line',
  /** "Food Manager — In person" against "Food Manager — Online". */
  LINE_DELIVERY: 'line_delivery',
  /** In person against online, across everything. */
  DELIVERY: 'delivery',
  /** One line per package. Only sensible with a package filter on. */
  PACKAGE: 'package',
};

/** What the chart plots. `units` is order_items.qty — one per registration. */
export const SERIES_METRIC = {
  REVENUE: 'revenue',
  UNITS: 'units',
};

/** Which window a comparison is measured against. */
export const COMPARE_TO = {
  /** The equally long window immediately before. Right for trailing ranges. */
  PREVIOUS_PERIOD: 'previous_period',
  /** The previous calendar month or year, whole. Right for "this month vs last month". */
  PREVIOUS_CALENDAR: 'previous_calendar',
  /** The same days one week earlier. */
  SAME_SPAN_LAST_WEEK: 'same_span_last_week',
  /** The same dates one month earlier: 1-15 Oct against 1-15 Sep. */
  SAME_SPAN_LAST_MONTH: 'same_span_last_month',
  /** The same dates one quarter earlier. */
  SAME_SPAN_LAST_QUARTER: 'same_span_last_quarter',
  /** The same dates one year earlier. */
  SAME_SPAN_LAST_YEAR: 'same_span_last_year',
};

export const ANALYTICS_PRESET = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  LAST_90_DAYS: 'last_90_days',
  MONTH_TO_DATE: 'month_to_date',
  YEAR_TO_DATE: 'year_to_date',
  LAST_12_MONTHS: 'last_12_months',
  CUSTOM: 'custom',
};

/** Which part of the overview to fetch. The headline is quick; the breakdowns are not. */
export const OVERVIEW_SECTION = {
  /** Totals, cash, payment methods and the two daily series. */
  SUMMARY: 'summary',
  /** Every breakdown, the split series and the risers and fallers. */
  DETAILS: 'details',
};

/** A failed analytics request, carrying the HTTP status so the screen can tell "no access" from "broken". */
export class AnalyticsRequestError extends Error {
  /**
   * @param {string} message Message to show.
   * @param {number|null} status HTTP status, or null when no response arrived.
   */
  constructor(message, status) {
    super(message);
    this.name = 'AnalyticsRequestError';
    this.status = status;
  }

  /** @returns {boolean} True when the caller is not allowed to see these figures. */
  get isDenied() {
    return this.status === 401 || this.status === 403;
  }
}

/**
 * Run a request and normalise its failure.
 * @param {Function} action Request.
 * @returns {Promise<any>}
 */
async function catchHttpError(action) {
  try {
    return await action();
  } catch (error) {
    if (!error?.isAxiosError) {
      throw error;
    }
    const status = error.response?.status ?? null;
    // A rejected window carries its explanation under `errors`; `message` is only the generic
    // "The given data was invalid."
    const firstFieldError = Object.values(error?.response?.data?.errors ?? {})[0]?.[0];
    const message = status === 422 && firstFieldError ? firstFieldError : error?.response?.data?.message;
    throw new AnalyticsRequestError(message || 'We could not reach the server. Please try again.', status);
  }
}

/**
 * Build the query for a window.
 *
 * A preset always wins server-side, so the two are never sent together.
 * @param {object} period Window: preset or startDate/endDate, plus compare, compareTo and limit.
 * `refresh` asks the server to rebuild the figures instead of answering from its cache.
 * @returns {object}
 */
function toQuery({
  preset, startDate, endDate, compare, compareTo, limit, seriesGroupBy, packageIds, sections, refresh,
}) {
  const params = {};

  if (preset && preset !== ANALYTICS_PRESET.CUSTOM) {
    params.preset = preset;
  } else if (startDate && endDate) {
    params.start_date = startDate;
    params.end_date = endDate;
  }

  if (compare) {
    params.compare = 1;
    if (compareTo) {
      params.compare_to = compareTo;
    }
  }
  if (limit) {
    params.limit = limit;
  }
  if (seriesGroupBy && seriesGroupBy !== SERIES_GROUP.TOTAL) {
    params.series_group_by = seriesGroupBy;
  }
  if (Array.isArray(packageIds) && packageIds.length > 0) {
    params.package_ids = packageIds;
  }
  if (sections) {
    params.sections = sections;
  }
  if (refresh) {
    params.refresh = 1;
  }

  return params;
}

/**
 * Map the window descriptor.
 * @param {object} dto Period DTO.
 * @returns {object}
 */
function mapPeriod(dto) {
  return {
    preset: dto.preset,
    startDate: dto.start_date,
    endDate: dto.end_date,
    days: dto.days,
    timezone: dto.timezone,
  };
}

/**
 * Map the response metadata.
 * @param {object} dto Meta DTO.
 * @returns {object}
 */
function mapMeta(dto) {
  return {
    // ISO 8601 with offset: when the server built these figures, which may be well before the
    // request when they came out of its cache.
    generatedAt: dto?.generated_at ?? null,
    cached: Boolean(dto?.cached),
  };
}

/**
 * Map booked totals.
 * @param {object} dto Bookings DTO.
 * @returns {object}
 */
function mapBookings(dto) {
  return {
    revenue: dto.revenue,
    gross: dto.gross,
    orderDiscount: dto.order_discount,
    tax: dto.tax,
    units: dto.units,
    orders: dto.orders,
    buyers: dto.buyers,
    averageOrderValue: dto.average_order_value,
    // True when a package filter narrowed these figures, in which case order-level discount and
    // tax are null rather than zero - absent, not nothing.
    isPackageFiltered: Boolean(dto.is_package_filtered),
  };
}

/**
 * Map one package row.
 * @param {object} dto Package DTO.
 * @returns {object}
 */
function mapPackage(dto) {
  return {
    packageId: dto.package_id,
    name: dto.name,
    productLine: dto.product_line,
    market: dto.market,
    marketSource: dto.market_source,
    revenue: dto.revenue,
    units: dto.units,
    orders: dto.orders,
    // Revenue divided by registrations, after every discount. Null when nothing sold.
    averagePrice: dto.average_price ?? null,
  };
}

/**
 * Map the storefront breakdown of a product line: the PFS checkout (order revenue) beside the Apex
 * site's legacy checkout (payments with no order).
 * @param {object} dto Storefronts DTO.
 * @returns {object}
 */
function mapStorefronts(dto) {
  return {
    pfsCheckout: {
      label: dto.pfs_checkout.label,
      revenue: dto.pfs_checkout.revenue,
      units: dto.pfs_checkout.units,
      orders: dto.pfs_checkout.orders,
      packages: (dto.pfs_checkout.packages ?? []).map(mapPackage),
    },
    apexSite: {
      label: dto.apex_site.label,
      amount: dto.apex_site.amount,
      payments: dto.apex_site.payments,
      seats: dto.apex_site.seats,
      packages: (dto.apex_site.packages ?? []).map(row => ({
        packageId: row.package_id,
        name: row.name,
        amount: row.amount,
        payments: row.payments,
        seats: row.seats,
      })),
    },
  };
}

/**
 * Map a rolled-up bucket (a product line or a market) with its own package list.
 * @param {object} dto Bucket DTO.
 * @returns {object}
 */
function mapBucket(dto) {
  const bucket = {
    key: dto.key,
    label: dto.label,
    revenue: dto.revenue,
    units: dto.units,
    orders: dto.orders,
    averagePrice: dto.average_price ?? null,
    packages: (dto.packages ?? []).map(mapPackage),
  };

  if (dto.storefronts) {
    bucket.storefronts = mapStorefronts(dto.storefronts);
    bucket.totalWithApexSite = dto.total_with_apex_site;
    bucket.unitsWithApexSite = dto.units_with_apex_site;
  }

  return bucket;
}

/**
 * Map a period-over-period change block.
 * @param {object} dto Change DTO.
 * @returns {object}
 */
function mapChange(dto) {
  return Object.entries(dto ?? {}).reduce((acc, [key, value]) => {
    acc[key] = { absolute: value.absolute, percent: value.percent };
    return acc;
  }, {});
}

/**
 * Map one mover row.
 *
 * Field names are kept as the server sends them for the change figures, because the template
 * ranks and labels off them directly and a second naming scheme here would only be a place for
 * the two to drift.
 * @param {object} dto Mover DTO.
 * @returns {object}
 */
function mapMover(dto) {
  return {
    package_id: dto.package_id,
    name: dto.name,
    product_line: dto.product_line,
    revenue: dto.revenue,
    previous_revenue: dto.previous_revenue,
    revenue_change: dto.revenue_change,
    // Null when the package had no prior sales - "first sales", not an infinite percentage.
    revenue_change_percent: dto.revenue_change_percent,
    units: dto.units,
    previous_units: dto.previous_units,
    units_change: dto.units_change,
    average_price: dto.average_price,
    previous_average_price: dto.previous_average_price,
    status: dto.status,
  };
}

/**
 * Map a collections summary.
 * @param {object} dto Collections DTO.
 * @returns {object}
 */
function mapCollections(dto) {
  return {
    collected: dto.collected,
    refunded: dto.refunded,
    net: dto.net,
    refundCount: dto.refund_count,
  };
}

/**
 * Map a figures block (amount, payments, seats).
 * @param {object} dto Figures DTO.
 * @returns {object}
 */
function mapFigures(dto) {
  return {
    amount: dto?.amount ?? 0,
    payments: dto?.payments ?? 0,
    seats: dto?.seats ?? 0,
  };
}

/**
 * Map collections that no order accounts for.
 * @param {object} dto Non-order DTO.
 * @returns {object}
 */
function mapNonOrder(dto) {
  return {
    total: mapFigures(dto.total),
    apexSite: mapFigures(dto.apex_site),
    // Everything outside the Apex division, with its category breakdown.
    other: {
      amount: dto.other?.amount ?? 0,
      payments: dto.other?.payments ?? 0,
      byCategory: (dto.other?.by_category ?? []).map(row => ({
        categoryId: row.category_id,
        label: row.label,
        ...mapFigures(row),
      })),
    },
  };
}

/**
 * Map one payment method row.
 * @param {object} row Method DTO.
 * @returns {object}
 */
function mapMethod(row) {
  return {
    key: row.key,
    label: row.label,
    isCard: row.is_card ?? false,
    collected: row.collected,
    refunded: row.refunded,
    net: row.net,
    share: row.share ?? null,
    paymentCount: row.payment_count,
    refundCount: row.refund_count,
  };
}

/**
 * Map the headline half of the overview.
 * @param {object} dto Overview DTO, or its summary section.
 * @returns {object}
 */
function mapOverviewSummary(dto) {
  return {
    period: mapPeriod(dto.period),
    bookings: mapBookings(dto.bookings),
    // Null while a package filter is on: payments are recorded against an order, never a line,
    // so there is no honest way to attribute cash to one package.
    collections: dto.collections ? mapCollections(dto.collections) : null,
    // Complete: these sum to everything collected in the window.
    paymentMethods: (dto.payment_methods?.methods ?? []).map(mapMethod),
    // The same money under the named methods, in a fixed order with the empty ones included.
    paymentsByMethod: (dto.payment_methods?.by_method ?? []).map(mapMethod),
    // A slice THROUGH the brands above, not an addition to them - an Apple Pay payment is a
    // Visa or a Mastercard and is already counted in that brand's row.
    paymentWallets: (dto.payment_methods?.wallets ?? []).map(row => ({
      key: row.key,
      label: row.label,
      fundingBrands: row.funding_brands ?? [],
      collected: row.collected,
      refunded: row.refunded,
      net: row.net,
      paymentCount: row.payment_count,
      refundCount: row.refund_count,
    })),
    series: {
      bookings: (dto.series?.bookings ?? []).map(row => ({
        day: row.day,
        revenue: row.revenue,
        units: row.units,
        orders: row.orders,
      })),
      collections: (dto.series?.collections ?? []).map(row => ({
        day: row.day,
        collected: row.collected,
        refunded: row.refunded,
        net: row.net,
      })),
    },
    // Non-null when the window reaches back before the orders stack went live. The dashboard
    // says "partly missing" rather than drawing a cliff as if revenue collapsed.
    coverageGap: dto.coverage_gap,
    comparison: dto.comparison
      ? {
        period: mapPeriod(dto.comparison.period),
        bookings: mapBookings(dto.comparison.bookings),
        // Only sent with the summary section, and only while no package filter is on.
        collections: dto.comparison.collections ? mapCollections(dto.comparison.collections) : null,
        change: mapChange(dto.comparison.change),
        coverageGap: dto.comparison.coverage_gap,
      }
      : null,
  };
}

/**
 * Map the breakdown half of the overview.
 * @param {object} dto Overview DTO, or its details section.
 * @returns {object}
 */
function mapOverviewDetails(dto) {
  return {
    series: {
      grouped: (dto.series?.grouped ?? []).map(entry => ({
        key: entry.key,
        label: entry.label,
        revenue: entry.revenue,
        units: entry.units,
        points: entry.points,
      })),
    },
    seriesGroupBy: dto.series_group_by,
    packageIds: dto.package_ids,
    productLines: (dto.product_lines ?? []).map(mapBucket),
    nonOrderCollections: dto.non_order_collections ? mapNonOrder(dto.non_order_collections) : null,
    markets: (dto.markets ?? []).map(mapBucket),
    packages: (dto.packages ?? []).map(mapPackage),
    packageCount: dto.package_count,
    comparison: dto.comparison
      ? {
        mode: dto.comparison.mode,
        movers: {
          risers: (dto.comparison.movers?.risers ?? []).map(mapMover),
          fallers: (dto.comparison.movers?.fallers ?? []).map(mapMover),
        },
      }
      : null,
  };
}

/**
 * Fetch one overview response.
 * @param {object} period Window.
 * @returns {Promise<object>} The raw body: the figures under `data` and their build time under `meta`.
 */
async function fetchOverview(period) {
  return catchHttpError(async () => {
    const response = await axiosService.get(appUrlsConfig.analytics.overview, { params: toQuery(period) });
    return response.data;
  });
}

/**
 * GET admin/analytics/overview, the whole response in one request.
 * @param {object} period Window.
 * @returns {Promise<object>}
 */
async function getOverview(period) {
  const body = await fetchOverview(period);
  const summary = mapOverviewSummary(body.data);
  const details = mapOverviewDetails(body.data);

  return {
    ...summary,
    ...details,
    series: { ...summary.series, ...details.series },
    comparison: summary.comparison ? { ...details.comparison, ...summary.comparison } : null,
    meta: mapMeta(body.meta),
  };
}

/**
 * GET admin/analytics/overview?sections=summary, the headline half, which answers quickly.
 * @param {object} period Window.
 * @returns {Promise<object>}
 */
async function getOverviewSummary(period) {
  const body = await fetchOverview({ ...period, sections: OVERVIEW_SECTION.SUMMARY });
  return { ...mapOverviewSummary(body.data), meta: mapMeta(body.meta) };
}

/**
 * GET admin/analytics/overview?sections=details, every breakdown, which takes longer.
 * @param {object} period Window.
 * @returns {Promise<object>}
 */
async function getOverviewDetails(period) {
  const body = await fetchOverview({ ...period, sections: OVERVIEW_SECTION.DETAILS });
  return { ...mapOverviewDetails(body.data), meta: mapMeta(body.meta) };
}

/**
 * Map a change pair, or null when nothing was compared.
 * @param {object|undefined} dto Change DTO.
 * @returns {object|null}
 */
function mapChangePair(dto) {
  return dto ? { absolute: dto.absolute, percent: dto.percent } : null;
}

/**
 * Map one cart combination, at package or category level.
 * @param {object} dto Combo DTO.
 * @returns {object}
 */
function mapCartCombo(dto) {
  return {
    key: dto.key,
    label: dto.label,
    // True for the Premium combo that carries the free Food Handler card.
    includesFreeCard: dto.includes_free_card,
    orders: dto.orders,
    share: dto.share,
    revenue: dto.revenue,
    averageOrderValue: dto.average_order_value,
    previousOrders: dto.previous_orders ?? null,
    previousRevenue: dto.previous_revenue ?? null,
    ordersChange: mapChangePair(dto.orders_change),
    revenueChange: mapChangePair(dto.revenue_change),
  };
}

/**
 * Map a block of figures and, when compared, its previous values and changes.
 * @param {object} dto Figures DTO.
 * @param {Function} mapGroup Maps one set of figures.
 * @returns {object}
 */
function mapWithChange(dto, mapGroup) {
  return {
    ...mapGroup(dto),
    previous: dto.previous ? mapGroup(dto.previous) : null,
    change: Object.fromEntries(Object.entries(dto.change ?? {}).map(([key, value]) => [
      key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase()),
      mapChangePair(value),
    ])),
  };
}

/**
 * Map the cart combinations section of the baskets response.
 * @param {object} dto Cart combos DTO.
 * @returns {object}
 */
function mapCartCombos(dto) {
  return {
    summary: mapWithChange(dto.summary, figures => ({
      customerOrders: figures.customer_orders,
      comboOrders: figures.combo_orders,
      singleOrders: figures.single_orders,
      comboRate: figures.combo_rate,
      comboRevenue: figures.combo_revenue,
      singleRevenue: figures.single_revenue,
      comboAverageOrderValue: figures.combo_average_order_value,
      singleAverageOrderValue: figures.single_average_order_value,
    })),
    // Premium Food Manager and the free Food Handler card that comes with it.
    premium: mapWithChange(dto.premium, figures => ({
      premiumOrders: figures.premium_orders,
      foodManagerOrders: figures.food_manager_orders,
      premiumShare: figures.premium_share,
      freeCards: figures.free_cards,
    })),
    combos: (dto.combos ?? []).map(mapCartCombo),
    categoryCombos: (dto.category_combos ?? []).map(mapCartCombo),
    comparison: dto.comparison
      ? {
        period: mapPeriod(dto.comparison.period),
        mode: dto.comparison.mode,
        coverageGap: dto.comparison.coverage_gap,
      }
      : null,
  };
}

/**
 * GET admin/analytics/baskets
 *
 * The cart combinations are the only part the dashboard reads. The product-line keys and the
 * customer-level views stay in the response for the native app.
 * @param {object} period Window.
 * @returns {Promise<object>}
 */
async function getBaskets(period) {
  return catchHttpError(async () => {
    const response = await axiosService.get(appUrlsConfig.analytics.baskets, { params: toQuery(period) });
    const dto = response.data.data;

    return {
      meta: mapMeta(response.data.meta),
      period: mapPeriod(dto.period),
      cart: mapCartCombos(dto.cart_combos),
      coverageGap: dto.coverage_gap,
    };
  });
}

/**
 * GET admin/analytics/daily — the Financial Breakdown Per Day.
 *
 * Method and product-line columns are whatever the window contains, so the table shape follows
 * the data rather than a hard-coded list of payment types.
 * @param {object} period Window.
 * @returns {Promise<object>}
 */
async function getDaily(period) {
  return catchHttpError(async () => {
    const response = await axiosService.get(appUrlsConfig.analytics.daily, { params: toQuery(period) });
    const dto = response.data.data;

    return {
      meta: mapMeta(response.data.meta),
      period: mapPeriod(dto.period),
      methods: dto.methods ?? [],
      // Method columns in the fixed display order: [{ key, label }].
      methodColumns: dto.method_columns ?? [],
      lines: dto.lines ?? [],
      days: (dto.days ?? []).map(row => ({
        day: row.day,
        collected: row.collected,
        refunded: row.refunded,
        net: row.net,
        paymentCount: row.payment_count,
        refundCount: row.refund_count,
        methods: row.methods ?? {},
        methodDetail: row.method_detail ?? {},
        lines: row.lines ?? {},
        applePay: row.apple_pay,
        unallocated: row.unallocated,
        apexSite: row.apex_site ?? 0,
      })),
      totals: {
        collected: dto.totals.collected,
        refunded: dto.totals.refunded,
        net: dto.totals.net,
        paymentCount: dto.totals.payment_count,
        refundCount: dto.totals.refund_count,
        methods: dto.totals.methods ?? {},
        methodDetail: dto.totals.method_detail ?? {},
        lines: dto.totals.lines ?? {},
        applePay: dto.totals.apple_pay,
        unallocated: dto.totals.unallocated,
        apexSite: dto.totals.apex_site ?? 0,
      },
      coverageGap: dto.coverage_gap,
    };
  });
}

/** How the bulk buyers list is grouped. */
export const BULK_GROUP = {
  /** One row per person who placed the orders. */
  BUYER: 'buyer',
  /** One row per company, folding together everyone who bought for it. */
  COMPANY: 'company',
};

/** What the server ranks the bulk buyers by before it keeps the top rows. */
export const BULK_RANK = {
  REVENUE: 'revenue',
  SEATS: 'seats',
  ORDERS: 'orders',
  LAST_PURCHASE: 'last_purchase',
};

/**
 * Build the bulk buyers query. Dates follow the analytics window rules; `allTime` replaces them.
 * @param {object} filters Window plus minSeats, groupBy, rankBy, limit, recentMonths, lapsedOnly, state.
 * @returns {object}
 */
function toBulkQuery({
  allTime, preset, startDate, endDate, minSeats, groupBy, rankBy, limit, recentMonths, lapsedOnly, state,
}) {
  const params = {};

  if (allTime) {
    params.all_time = 1;
  } else if (preset && preset !== ANALYTICS_PRESET.CUSTOM) {
    params.preset = preset;
  } else if (startDate && endDate) {
    params.start_date = startDate;
    params.end_date = endDate;
  }

  Object.entries({
    min_seats: minSeats, group_by: groupBy, rank_by: rankBy, limit, recent_months: recentMonths,
  }).forEach(([key, value]) => {
    if (value) {
      params[key] = value;
    }
  });

  if (lapsedOnly) {
    params.lapsed_only = 1;
  }
  if (state) {
    params.state = state;
  }

  return params;
}

/**
 * Map one bulk buyer row.
 * @param {object} dto Buyer DTO.
 * @returns {object}
 */
function mapBulkBuyer(dto) {
  return {
    key: dto.key,
    name: dto.name,
    company: dto.company,
    contactName: dto.contact_name,
    email: dto.email,
    phone: dto.phone,
    state: dto.state,
    bulkOrders: dto.bulk_orders,
    seats: dto.seats,
    revenue: dto.revenue,
    products: dto.products ?? [],
    productCount: dto.product_count,
    firstPurchase: dto.first_purchase,
    lastPurchase: dto.last_purchase,
    daysSinceLast: dto.days_since_last,
    isRecent: dto.is_recent,
    lifetimeOrders: dto.lifetime_orders,
    lifetimeSeats: dto.lifetime_seats,
    lifetimeRevenue: dto.lifetime_revenue,
  };
}

/**
 * GET admin/analytics/bulk-buyers
 *
 * Customer contact details: the server limits this to PFS administrators and the response is not
 * cached anywhere, by the server or the browser.
 * @param {object} filters Window and list options.
 * @returns {Promise<object>}
 */
async function getBulkBuyers(filters) {
  return catchHttpError(async () => {
    const response = await axiosService.get(appUrlsConfig.analytics.bulkBuyers, { params: toBulkQuery(filters) });
    const { data, meta } = response.data;

    return {
      isBuilt: meta.built,
      latestPurchaseAt: meta.latest_purchase_at,
      totals: data.totals
        ? {
          buyers: data.totals.buyers,
          bulkOrders: data.totals.bulk_orders,
          seats: data.totals.seats,
          revenue: data.totals.revenue,
          recentBuyers: data.totals.recent_buyers,
          lapsedBuyers: data.totals.lapsed_buyers,
          shown: data.totals.shown,
        }
        : null,
      buyers: (data.buyers ?? []).map(mapBulkBuyer),
    };
  });
}

export const analyticsService = {
  getBulkBuyers,
  getOverview,
  getOverviewSummary,
  getOverviewDetails,
  getBaskets,
  getDaily,
};
